// Revenue schedule detail: the recognition timetable with a recognize-next
// action that posts the next period and rolls forward.
import { formatMoney } from "@sylviq/ui-core";
import { Button } from "@sylviq/ui-react-web/components/ui/button";
import { Dialog } from "@sylviq/ui-react-web/components/ui/dialog";
import { PageHeader } from "@sylviq/ui-react-web/components/ui/page-header";
import {
  StatusDot,
  type StatusTone,
} from "@sylviq/ui-react-web/components/ui/status-dot";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@sylviq/ui-react-web/components/ui/table";
import { Link, useParams } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { CardField } from "@/components/RecordCard";
import {
  getRevenueSchedule,
  type RevenueSchedule,
  type RevenueScheduleEntry,
  recognizeRevenueEntry,
} from "@/lib/api";
import { humanizeLabel, useInvoiceNumbers } from "@/lib/labels";

function statusTone(status: string): StatusTone {
  switch (status.toLowerCase()) {
    case "completed":
      return "success";
    case "active":
      return "info";
    case "cancelled":
      return "danger";
    default:
      return "idle";
  }
}

function plainStatus(status: string): string {
  const lower = status.toLowerCase();
  return lower.charAt(0).toUpperCase() + lower.slice(1);
}

function toErrorMessage(cause: unknown): string {
  return cause instanceof Error ? cause.message : String(cause);
}

/** Earned/upcoming marker for one period, linked to its journal when one
 * exists. Shared by the desktop timetable and the mobile period cards. */
function EntryStatus({ entry }: { entry: RevenueScheduleEntry }) {
  if (!entry.recognized) return <StatusDot tone="idle" label="Upcoming" />;
  return entry.journal_entry_id ? (
    <Link
      to="/journal/$entryId"
      params={{ entryId: entry.journal_entry_id }}
      className="text-xs text-[var(--muted-foreground)] underline-offset-4 hover:underline"
    >
      <StatusDot tone="success" label="Earned" />
    </Link>
  ) : (
    <StatusDot tone="success" label="Earned" />
  );
}

export function RevenueScheduleDetailPage() {
  const { revenueScheduleId } = useParams({ strict: false }) as {
    revenueScheduleId: string;
  };
  const [schedule, setSchedule] = useState<RevenueSchedule | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const invoiceNumbers = useInvoiceNumbers();

  const reload = useCallback(() => {
    if (!revenueScheduleId) return;
    setError(null);
    getRevenueSchedule(revenueScheduleId)
      .then(setSchedule)
      .catch((cause: unknown) => setError(toErrorMessage(cause)));
  }, [revenueScheduleId]);

  useEffect(reload, [reload]);

  const recognize = async () => {
    if (!revenueScheduleId) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const result = await recognizeRevenueEntry(revenueScheduleId);
      setNotice(
        `Recognized the next period (entry ${result.journal_entry_id}).`,
      );
      reload();
    } catch (cause) {
      setError(`Recognize failed: ${toErrorMessage(cause)}`);
    } finally {
      setBusy(false);
    }
  };

  const confirm = () => {
    setConfirming(false);
    void recognize();
  };

  const canRecognize =
    schedule !== null &&
    schedule.status.toLowerCase() === "active" &&
    schedule.entries.some((entry) => !entry.recognized);

  return (
    <div className="grid gap-6">
      <nav aria-label="Breadcrumb" className="text-sm">
        <Link
          to="/revenue-schedules"
          className="text-[var(--muted-foreground)] hover:text-[var(--foreground)] hover:underline"
        >
          Revenue schedules
        </Link>
        <span
          aria-hidden="true"
          className="mx-2 text-[var(--muted-foreground)]"
        >
          /
        </span>
        <span aria-current="page" className="font-medium">
          {schedule
            ? (invoiceNumbers.get(schedule.invoice_id) ?? "Schedule")
            : "Schedule"}
        </span>
      </nav>

      {error ? (
        <div
          role="alert"
          className="surface rounded-xl border border-[var(--danger)] p-4 text-sm text-[var(--danger)]"
        >
          {error}
          <div className="mt-2">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={reload}
            >
              Retry
            </Button>
          </div>
        </div>
      ) : null}

      {notice ? (
        <output className="surface block rounded-xl border border-[var(--success)] p-4 text-sm text-[var(--success)]">
          {notice}
        </output>
      ) : null}

      {schedule === null && !error ? (
        <p className="text-sm text-[var(--muted-foreground)]">Loading…</p>
      ) : schedule ? (
        <>
          <PageHeader
            title={
              invoiceNumbers.get(schedule.invoice_id)
                ? `Schedule for invoice ${invoiceNumbers.get(schedule.invoice_id)}`
                : "Revenue schedule"
            }
            description={
              <>
                {new Date(schedule.start_date).toLocaleDateString()} →{" "}
                {new Date(schedule.end_date).toLocaleDateString()} ·{" "}
                {humanizeLabel(schedule.method)} ·{" "}
                <StatusDot
                  tone={statusTone(schedule.status)}
                  label={plainStatus(schedule.status)}
                />
              </>
            }
            actions={
              canRecognize ? (
                <Button
                  type="button"
                  size="sm"
                  onClick={() => setConfirming(true)}
                  disabled={busy}
                  title="Post the next period (moves its share from deferred to earned revenue)"
                >
                  {busy ? "Recognizing…" : "Recognize next period"}
                </Button>
              ) : null
            }
          />

          <div className="grid gap-4 sm:grid-cols-3">
            <div className="surface rounded-2xl p-5">
              <p className="text-xs font-medium text-[var(--muted-foreground)]">
                Total to spread
              </p>
              <p className="mt-1 text-2xl font-semibold tabular-nums">
                {formatMoney({ cents: schedule.total_minor, currency: "USD" })}
              </p>
            </div>
            <div className="surface rounded-2xl p-5">
              <p className="text-xs font-medium text-[var(--muted-foreground)]">
                Earned so far
              </p>
              <p className="mt-1 text-2xl font-semibold tabular-nums">
                {formatMoney({
                  cents: schedule.recognized_minor,
                  currency: "USD",
                })}
              </p>
            </div>
            <div className="surface rounded-2xl p-5">
              <p className="text-xs font-medium text-[var(--muted-foreground)]">
                Invoice
              </p>
              <p className="mt-1 text-sm">
                <Link
                  to="/invoices/$invoiceId"
                  params={{ invoiceId: schedule.invoice_id }}
                  className="text-xs text-[var(--action)] underline-offset-4 hover:underline"
                  title={schedule.invoice_id}
                >
                  {invoiceNumbers.get(schedule.invoice_id)
                    ? `View invoice ${invoiceNumbers.get(schedule.invoice_id)}`
                    : "View invoice"}{" "}
                  →
                </Link>
              </p>
            </div>
          </div>

          <div
            className="surface hidden overflow-hidden rounded-2xl min-[64rem]:block"
            data-testid="revenue-schedule-entries-table"
          >
            <div className="table-scroll">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Period</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {schedule.entries.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={3}>
                        This schedule has no periods yet.
                      </TableCell>
                    </TableRow>
                  ) : (
                    schedule.entries.map((entry) => (
                      <TableRow key={entry.id}>
                        <TableCell className="whitespace-nowrap">
                          {new Date(entry.period_date).toLocaleDateString()}
                        </TableCell>
                        <TableCell>
                          <EntryStatus entry={entry} />
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {formatMoney({
                            cents: entry.amount_minor,
                            currency: "USD",
                          })}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </div>

          <div
            className="stack-list grid gap-3 min-[64rem]:hidden"
            data-testid="revenue-schedule-entries-stack"
          >
            {schedule.entries.length === 0 ? (
              <p className="text-sm text-[var(--muted-foreground)]">
                This schedule has no periods yet.
              </p>
            ) : (
              schedule.entries.map((entry) => (
                <article
                  key={entry.id}
                  className="stack-card rounded-xl border border-[var(--border)] bg-[var(--muted)] p-4"
                >
                  <div className="stack-card-heading flex flex-wrap items-center justify-between gap-4 text-sm">
                    <span className="record-title">
                      {new Date(entry.period_date).toLocaleDateString()}
                    </span>
                    <span className="tabular-nums">
                      {formatMoney({
                        cents: entry.amount_minor,
                        currency: "USD",
                      })}
                    </span>
                  </div>
                  <CardField label="Status">
                    <EntryStatus entry={entry} />
                  </CardField>
                </article>
              ))
            )}
          </div>
        </>
      ) : null}

      <Dialog
        open={confirming}
        onOpenChange={(open) => {
          if (!open) setConfirming(false);
        }}
        title="Recognize the next period?"
        description="This posts a revenue recognition journal for that period — moving its share from deferred revenue to earned revenue. Each confirmation recognizes one period."
      >
        <div className="actions-row">
          <Button
            type="button"
            variant="secondary"
            onClick={() => setConfirming(false)}
          >
            Cancel
          </Button>
          <Button type="button" onClick={confirm}>
            Recognize revenue
          </Button>
        </div>
      </Dialog>
    </div>
  );
}
